import type { GoalDriftSummary, GoalItem } from "@/lib/api"

export const MAX_RECOVERY_GOALS = 2
export const MAX_STEADY_GOALS = 4

function sortGoalsForFocus(goals: GoalItem[]): GoalItem[] {
  return [...goals].sort((left, right) => {
    const progressDelta = right.progress_pct - left.progress_pct
    if (Math.abs(progressDelta) > 0.01) return progressDelta

    const milestoneDelta = right.milestones.length - left.milestones.length
    if (milestoneDelta !== 0) return milestoneDelta

    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  })
}

function keepFocusedGoalVisible(
  goals: GoalItem[],
  allGoals: GoalItem[],
  focusGoalId: string | null,
  limit: number
): GoalItem[] {
  if (!focusGoalId || goals.some((goal) => goal.id === focusGoalId)) {
    return goals
  }

  const focusedGoal = allGoals.find((goal) => goal.id === focusGoalId)
  if (!focusedGoal) return goals

  return [focusedGoal, ...goals.filter((goal) => goal.id !== focusGoalId)].slice(0, limit)
}

export function buildFocusedGoalGroups({
  goals,
  goalDriftSummary,
  focusGoalId,
  showFullInventory,
}: {
  goals: GoalItem[]
  goalDriftSummary: GoalDriftSummary | null
  focusGoalId: string | null
  showFullInventory: boolean
}) {
  const recoveryGoals = (goalDriftSummary?.top_drifting_goals ?? [])
    .map((goal) => goals.find((item) => item.id === goal.id))
    .filter((goal): goal is GoalItem => Boolean(goal))
  const recoveryGoalIds = new Set(recoveryGoals.map((goal) => goal.id))
  const steadyFocusGoalId = focusGoalId && recoveryGoalIds.has(focusGoalId) ? null : focusGoalId
  const allSteadyGoals = sortGoalsForFocus(goals.filter((goal) => !recoveryGoalIds.has(goal.id)))

  const steadyGoals = (
    goalDriftSummary?.top_steady_goals?.length
      ? goalDriftSummary.top_steady_goals
          .map((goal) => goals.find((item) => item.id === goal.id))
          .filter((goal): goal is GoalItem => Boolean(goal))
      : allSteadyGoals
  )

  if (showFullInventory) {
    return {
      visibleRecoveryGoals: keepFocusedGoalVisible(recoveryGoals, goals, focusGoalId, Math.max(recoveryGoals.length, 1_000)),
      visibleSteadyGoals: keepFocusedGoalVisible(allSteadyGoals, goals, steadyFocusGoalId, Math.max(allSteadyGoals.length, 1_000)),
      hiddenRecoveryCount: 0,
      hiddenSteadyCount: 0,
      hiddenActiveCount: 0,
    }
  }

  const visibleRecoveryGoals = keepFocusedGoalVisible(
    recoveryGoals.slice(0, MAX_RECOVERY_GOALS),
    goals,
    focusGoalId,
    MAX_RECOVERY_GOALS
  )
  const visibleRecoveryIds = new Set(visibleRecoveryGoals.map((goal) => goal.id))
  const visibleSteadyGoals = keepFocusedGoalVisible(
    steadyGoals.filter((goal) => !visibleRecoveryIds.has(goal.id)).slice(0, MAX_STEADY_GOALS),
    goals,
    steadyFocusGoalId,
    MAX_STEADY_GOALS
  )

  return {
    visibleRecoveryGoals,
    visibleSteadyGoals,
    hiddenRecoveryCount: Math.max(recoveryGoals.length - visibleRecoveryGoals.length, 0),
    hiddenSteadyCount: Math.max(steadyGoals.length - visibleSteadyGoals.length, 0),
    hiddenActiveCount: Math.max(goals.length - visibleRecoveryGoals.length - visibleSteadyGoals.length, 0),
  }
}
